/**
 * CampaignDetailPage.jsx — Week 3 Fix
 * - Decision filter bar (All / CONTINUE / PAUSE / STOP) with counts
 * - Full KPI grid per history card (formatted, colour-coded, trend icon)
 * - Load more pagination
 * - Better stat header cards
 * - Timeline-style history with date separators
 * - Expanded/collapsed history cards isRTL
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { functions, db } from "../../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import Navigation from "../../components/layout/Navigation";
import EvaluateCampaignModal from "../../components/campaigns/EvaluateCampaignModal";
import DecisionResult from "../../components/campaigns/DecisionResult";
import ShareDecisionModal from "../../components/campaigns/ShareDecisionModal";
import { usePDFExport } from "../../hooks/usePDFExport";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useQuotas } from "../../hooks/useQuotas";

import { useDarkMode } from "../../hooks/useDarkMode";
import { FiArrowLeft, FiTrendingUp, FiTarget, FiClock,
 FiCheckCircle, FiXCircle, FiPauseCircle,
 FiShare2, FiChevronDown, FiChevronUp,
 FiFilter, FiRefreshCw, FiCalendar, FiBarChart2, FiDownload,
 FiLock,
} from "react-icons/fi";

/* ── constants ───────────────────────────────────────────── */
const DECISION_STYLES = {
 CONTINUE: { icon: <FiCheckCircle />, cls: "text-green-400",  bg: "bg-green-500/10 border-green-500/30"  },
 ADJUST:   { icon: <FiPauseCircle />, cls: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
 PAUSE:    { icon: <FiPauseCircle />, cls: "text-yellow-400", bg: "bg-yellow-500/10 border-yellow-500/30" },
 STOP:     { icon: <FiXCircle />,     cls: "text-red-400",    bg: "bg-red-500/10 border-red-500/30"       },
};

const FILTER_OPTIONS = ["all", "CONTINUE", "ADJUST", "STOP"];
const PAGE_SIZE = 5;

const KPI_SKIP = new Set([
  'spend','revenue','leads','conversions','targetCPL','targetCPA',
  'grossProfit','returnsLoss','profitWarning','profitWarningMsg',
  'revenueEst','revenueActual','ltv','ltvToCac','roi',
  'cpm','conversionRate','avgTicket','fees','shipping',
]);

// Currency-aware KPI meta. Built per-call so EN renders SAR and AR
// renders ر.س — matches the rest of the dashboard now that it's
// fully localized.
function buildKpiMeta(cur) {
  return {
    roas:            { fmt: v => `${v.toFixed(2)}x`,                good: v => v >= 1.5  },
    profit:          { fmt: v => `${Math.abs(v).toFixed(2)} ${cur}`, good: v => v > 0     },
    cpl:             { fmt: v => `${v.toFixed(2)} ${cur}`,           good: v => v < 50    },
    cac:             { fmt: v => `${v.toFixed(2)} ${cur}`,           good: v => v < 200   },
    cpa:             { fmt: v => `${v.toFixed(2)} ${cur}`,           good: v => v < 100   },
    conversion_rate: { fmt: v => `${v.toFixed(1)}%`,                good: v => v >= 5    },
    conversionPct:   { fmt: v => `${v.toFixed(1)}%`,                good: v => v >= 10   },
    ctr:             { fmt: v => `${v.toFixed(2)}%`,                good: v => v >= 2    },
    cpc:             { fmt: v => `${v.toFixed(2)} ${cur}`,           good: v => v < 3     },
    customers:       { fmt: v => `${Math.round(v)}`,                good: v => v > 0     },
    profitMargin:    { fmt: v => `${v.toFixed(1)}%`,                good: v => v >= 20   },
    returnRate:      { fmt: v => `${v.toFixed(1)}%`,                good: v => v < 10    },
  };
}

function fmtKPI(key, val, lang = "en") {
 // null / undefined → "—" (em-dash). Engines deliberately emit `null`
 // for metrics they couldn't compute (e.g. Agency without conversions
 // sets cac=null). We show the row anyway so the user sees the metric
 // exists but distinguish it from a real 0 with the dash.
 if (val === null || val === undefined) return "—";
 if (typeof val !== "number") return String(val);
 const cur = lang === "ar" ? "ر.س" : "SAR";
 const meta = buildKpiMeta(cur);
 const m = meta[key];
 if (m) return m.fmt(val);
 if (key.includes("rate") || key.includes("ctr") || key.includes("Pct")) return `${val.toFixed(1)}%`;
 if (key.includes("roas"))                                                  return `${val.toFixed(2)}x`;
 if (key.includes("profit") || key.includes("cpl") || key.includes("cac") || key.includes("cpa"))
   return `${Math.abs(val).toFixed(2)} ${cur}`;
 if (key === "customers")                                                    return `${Math.round(val)}`;
 return val.toFixed(2);
}

function kpiGood(key, val) {
 // For null/undefined we return true — the calling code uses this to
 // pick red vs green; null KPIs should render in neutral colour, and
 // returning true keeps them out of the red branch. The "—" string
 // already signals the missing state visually.
 if (val === null || val === undefined) return true;
 if (typeof val !== "number") return true;
 // "good" thresholds are currency-agnostic (numeric only) so the EN
 // meta works for AR too.
 return buildKpiMeta("SAR")[key]?.good(val) ?? true;
}

function kpiLabel(key, lang = "en") {
 // Lang-aware KPI labels. Previously this map was Arabic-only, which
 // meant the history-card KPIs in /campaigns/:id rendered Arabic
 // strings (e.g. "تكلفة العميل المحتمل") even when the dashboard
 // language was English. The English map mirrors the keys exactly so
 // any future KPI added needs both translations.
 const labels = {
   ar: {
     roas:          "العائد على الإنفاق الإعلاني",
     profit:        "الربح",
     cpl:           "تكلفة العميل المحتمل",
     cac:           "تكلفة اكتساب العميل",
     cpa:           "تكلفة الاكتساب",
     ctr:           "CTR",
     cpc:           "تكلفة النقرة",
     customers:     "العملاء الدافعون",
     conversionPct: "معدل التحويل",
     profitMargin:  "هامش الربح",
     returnRate:    "معدل الإرجاع",
   },
   en: {
     roas:          "ROAS",
     profit:        "Profit",
     cpl:           "Cost per Lead",
     cac:           "Customer Acquisition Cost",
     cpa:           "Cost per Action",
     ctr:           "CTR",
     cpc:           "Cost per Click",
     customers:     "Paying Customers",
     conversionPct: "Conversion Rate",
     profitMargin:  "Profit Margin",
     returnRate:    "Return Rate",
   },
 };
 const map = labels[lang] || labels.en;
 // Final fallback: prettify the raw key (camelCase / snake_case → words)
 return map[key]
   || key.replace(/_/g, " ")
         .replace(/([a-z])([A-Z])/g, "$1 $2")
         .replace(/\b\w/g, c => c.toUpperCase());
}

// Status badge label + colour helpers
// Status labels are lang-aware: English mode shows "Opportunity" /
// "Stable" / "Critical", Arabic mode shows فرصة / مستقر / حرج. Earlier
// this was an Arabic-only map (`STATUS_LABELS_AR`) and the badge in
// the history card rendered Arabic strings even when the rest of the
// page was English.
const STATUS_LABELS = {
  en: { Stable: "Stable", Opportunity: "Opportunity", Critical: "Critical",
        Excellent: "Excellent", Good: "Good", Fair: "Fair", Poor: "Poor" },
  ar: { Stable: "مستقر", Opportunity: "فرصة", Critical: "حرج",
        Excellent: "ممتاز", Good: "جيد", Fair: "مقبول", Poor: "ضعيف" },
};
function getStatusLabel(status, lang) {
  return (STATUS_LABELS[lang] || STATUS_LABELS.en)[status] || status;
}
function getStatusBadgeCls(status) {
  if (status === "Stable"      || status === "Excellent" || status === "Good")  return "bg-green-500/20 text-green-400 border border-green-500/40";
  if (status === "Opportunity" || status === "Fair")                             return "bg-yellow-500/20 text-yellow-400 border border-yellow-500/40";
  if (status === "Critical"    || status === "Poor")                            return "bg-red-500/20 text-red-400 border border-red-500/40";
  return "bg-gray-500/20 text-gray-400 border border-gray-500/40";
}


/* ── Bilingual helpers (same as DecisionResult) ─────────────── */
const NEXT_ACTIONS_AR = {
  'Scale budget 20-30% to maximize returns':      'زِد الميزانية 20-30% لتعظيم العائد',
  'Continue A/B testing creatives':               'استمر في اختبار A/B للإعلانات',
  'Monitor daily and adjust bids':                'راقب يومياً وعدّل العروض',
  'Monitor top-performing products':              'راقب المنتجات الأعلى أداءً',
  'Implement upselling strategies':               'طبّق استراتيجيات البيع الإضافي',
  'Review inventory levels':                      'راجع مستويات المخزون',
  'Optimize lead generation channels':            'حسّن قنوات توليد العملاء المحتملين',
  'Invest in customer retention':                 'استثمر في الاحتفاظ بالعملاء',
  'Track LTV by segment':                         'تتبع القيمة الدائمة حسب الشريحة',
  'Pause campaign and analyze data':              'أوقف الحملة مؤقتاً وحلّل البيانات',
  'Review targeting and audience':                'راجع الاستهداف والجمهور',
  'Test new creatives before resuming':           'اختبر إعلانات جديدة قبل الاستئناف',
  'Evaluate budget allocation':                   'قيّم توزيع الميزانية',
  'Stop campaign immediately':                    'أوقف الحملة فوراً',
  'Conduct post-mortem analysis':                 'أجرِ تحليلاً شاملاً للأداء',
  'Revise strategy before relaunch':              'راجع الاستراتيجية قبل إعادة الإطلاق',
  'Analyze what failed for future campaigns':     'حلّل أسباب الفشل للحملات المستقبلية',
};
function getLocalizedReason(dec, lang) {
  if (lang === 'ar') return dec.reasonAr || dec.reason || '';
  return dec.reason || '';
}
function getLocalizedActions(dec, lang) {
  if (lang === 'ar') {
    if (dec.nextActionsAr && dec.nextActionsAr.length > 0) return dec.nextActionsAr;
    return (dec.nextActions || []).map(a => NEXT_ACTIONS_AR[a] || a);
  }
  return dec.nextActions || [];
}

/* ─────────────────────────────────────────────────────────── */
const CampaignDetailPage = () => {
 const { campaignId } = useParams();
 const navigate = useNavigate();
 const { user, userData } = useAuth();
 const { generatePDF, generating: pdfGenerating } = usePDFExport();

 // Feature-flag + quota gates for the per-decision PDF/share buttons in
 // the history pane. Without these checks, a user on the Free plan
 // (which has pdf_enabled=false and pdf_monthly=0) could still click
 // "Download PDF" on every saved decision — the modal blocks them up
 // top, but this list bypassed it. The buttons now show a lock icon
 // and an explanatory tooltip when the user can't use them.
 const { canDownloadPDF, canShare } = useFeatureFlags();
 const { getUsage }                  = useQuotas();
 const pdfFeatureEnabled  = canDownloadPDF();
 const shareFeatureEnabled = canShare();
 // PDF quota check: read from the live useQuotas hook so the UI updates
 // in real time as the user generates more PDFs in this session.
 const pdfUsage   = getUsage("pdf");
 const shareUsage = getUsage("share");
 const pdfQuotaOk =
   !pdfUsage ||
   pdfUsage.quota === -1 ||                       // unlimited
   (pdfUsage.quota > 0 && pdfUsage.current < pdfUsage.quota);
 const shareQuotaOk =
   !shareUsage ||
   shareUsage.quota === -1 ||
   (shareUsage.quota > 0 && shareUsage.current < shareUsage.quota);
 const canPDFNow   = pdfFeatureEnabled  && pdfQuotaOk;
 const canShareNow = shareFeatureEnabled && shareQuotaOk;
 const { darkMode } = useDarkMode();
 const { t, isRTL, lang } = useLanguage();

 const [campaign, setCampaign] = useState(null);
 const [decisions, setDecisions] = useState([]);
 const [bizType, setBizType] = useState("agency");
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [refreshing, setRefreshing] = useState(false);

 // Modals
 const [showEvalModal, setShowEvalModal] = useState(false);
 const [showResultModal, setShowResultModal] = useState(false);
 const [showShareModal, setShowShareModal] = useState(false);
 const [latestResult, setLatestResult] = useState(null);
 const [shareTarget, setShareTarget] = useState(null);

 // History UI
 const [decisionFilter, setDecisionFilter] = useState("all");
 const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
 const [expanded, setExpanded] = useState({}); // id → bool

 const locale = lang === "ar" ? "ar-SA" : "en-US";

 /* ── data ──────────────────────────────────────────────── */
 const loadAll = useCallback(async (isRefresh = false) => {
 if (!user || !campaignId || !userData) return;
 if (isRefresh) setRefreshing(true); else setLoading(true);
 setError("");
 try {
 try {
 const byId = httpsCallable(functions, "getCampaignById");
 const hist = httpsCallable(functions, "getCampaignHistory");
 const [cRes, hRes] = await Promise.all([
 byId({ campaignId }),
 hist({ campaignId, limit: 50 }),
 ]);
 setCampaign(cRes.data.campaign);
 setDecisions(hRes.data.decisions || []);
 } catch {
 const snap = await getDoc(doc(db, "campaigns", campaignId));
 if (!snap.exists()) throw new Error(t("campaign_not_found"));
 setCampaign({ id: snap.id, ...snap.data() });
 setDecisions([]);
 }
 } catch (err) { setError(err.message || t("error")); }
 finally { setLoading(false); setRefreshing(false); }
 }, [user, campaignId, userData]);

 useEffect(() => {
 loadAll();
 // load bizType
 (async () => {
 try {
 if (userData?.tenantId) {
 const snap = await getDoc(doc(db, "tenants", userData.tenantId));
 if (snap.exists()) setBizType(snap.data().businessType || "agency");
 }
 } catch {}
 })();
 }, [loadAll]);

 /* ── helpers ───────────────────────────────────────────── */
 const formatDate = (ts, full = false) => {
 try {
 const d = ts?.seconds ? new Date(ts.seconds * 1000) : ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
 if (!d || isNaN(d.getTime())) return "—";
 return full
 ? d.toLocaleString(locale, { year:"numeric", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })
 : d.toLocaleDateString(locale, { year:"numeric", month:"short", day:"numeric" });
 } catch { return "—"; }
 };

 const dayLabel = (ts) => {
 try {
 if (!ts) return ""; // missing timestamp → no header label
 const d = ts?.seconds ? new Date(ts.seconds * 1000)
        : ts?.toDate    ? ts.toDate()
        : ts instanceof Date ? ts
        : new Date(ts);
 // Guard against "Invalid Date" — seen on freshly-evaluated decisions
 // returned by evaluateCampaign which don't carry createdAt yet
 // (the doc has serverTimestamp but the response shape is built
 // pre-write). Without this guard the component renders the literal
 // string "Invalid Date" as the section header.
 if (isNaN(d.getTime())) return "";
 const today = new Date(); today.setHours(0,0,0,0);
 const yesterday = new Date(today); yesterday.setDate(yesterday.getDate()-1);
 d.setHours(0,0,0,0);
 if (d.getTime() === today.getTime()) return lang === "ar" ? "اليوم" : "Today";
 if (d.getTime() === yesterday.getTime()) return lang === "ar" ? "أمس" : "Yesterday";
 return d.toLocaleDateString(locale, { weekday:"long", month:"short", day:"numeric" });
 } catch { return ""; }
 };

 /* ── filtered + grouped ─────────────────────────────────── */
 const filtered = useMemo(() => {
 const list = decisionFilter === "all" ? decisions : decisions.filter(d => d.decision === decisionFilter);
 return list;
 }, [decisions, decisionFilter]);

 const visible = filtered.slice(0, visibleCount);

 // Group by day for date separators
 const grouped = useMemo(() => {
 const groups = [];
 let lastDay = null;
 visible.forEach(d => {
 const day = dayLabel(d.createdAt);
 if (day !== lastDay) { groups.push({ type: "day", label: day }); lastDay = day; }
 groups.push({ type: "decision", data: d });
 });
 return groups;
 }, [visible]);

 const counts = useMemo(() => {
 const c = { all: decisions.length };
 decisions.forEach(d => {
   // Fold legacy PAUSE into the canonical ADJUST so counts match what
   // the filter pills and decision-style map use.
   const key = d.decision === "PAUSE" ? "ADJUST" : d.decision;
   c[key] = (c[key] || 0) + 1;
 });
 return c;
 }, [decisions]);

 /* ── modals ────────────────────────────────────────────── */
 const handleEvalSuccess = (result) => {
 setLatestResult(result);
 setShowEvalModal(false);
 setShowResultModal(true);
 loadAll(true);
 };
 const handleShare = (dec) => { setShareTarget(dec); setShowShareModal(true); };
 const toggleExpanded = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

 /* ── guards ─────────────────────────────────────────────── */
 if (loading) return (
 <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" : "bg-gradient-to-br from-gray-50 via-white to-gray-100"}`}>
 <Navigation />
 <div className="flex items-center justify-center h-96">
 <div className="text-center space-y-3">
 <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"}`}>{t("loading_campaign")}</p>
 </div>
 </div>
 </div>
 );

 if (error || !campaign) return (
 <div className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
 <Navigation />
 <div className="flex items-center justify-center h-96">
 <div className="text-center space-y-4">
 <div className="bg-red-500/20 text-red-400 px-6 py-4 rounded-xl">{error || t("campaign_not_found")}</div>
 <button onClick={() => navigate("/campaigns")} className={`text-blue-400 hover:text-blue-300 flex items-center gap-2 mx-auto `}>
 <FiArrowLeft className={""} />{t("back_to_campaigns")}
 </button>
 </div>
 </div>
 </div>
 );

 /* ── render isRTL ─────────────────────────────────────────────── */
 return (
 <div  className={`min-h-screen ${darkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white" : "bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900"}`}>
 <Navigation />
 <div className="p-4 md:p-8 max-w-5xl mx-auto">

 {/* ── Back + header ────────────────────────────── */}
 <div className="mb-8">
 <button onClick={() => navigate("/campaigns")} className={`flex items-center gap-2  ${darkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"} mb-4 transition-colors`}>
 <FiArrowLeft  />{t("back_to_campaigns")}
 </button>
 <div className={`flex items-start justify-between `}>
 <div >
 <div className={`flex items-center gap-3 mb-1 flex-wrap `}>
 <h1 className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{campaign.name}</h1>
 <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm  ${campaign.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}`}>
 <div className={`w-2 h-2 rounded-full ${campaign.status === "active" ? "bg-green-400 animate-pulse" : "bg-gray-400"}`} />
 {campaign.status === "active" ? t("status_active") : campaign.status || t("status_active")}
 </span>
 </div>
 {campaign.channel && (
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-sm`}>{t("channel")}: <span className={`${darkMode ? "text-white" : "text-gray-900"} capitalize`}>{campaign.channel}</span></p>
 )}
 </div>
 {/* isRTL */}
 <div className={`flex items-center gap-2 flex-shrink-0 `}>
 <button onClick={() => loadAll(true)} disabled={refreshing}
 className={`p-2.5 ${darkMode ? "bg-gray-700/60 hover:bg-gray-700 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-600"} rounded-xl transition-colors`}>
 <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
 </button>
 <button onClick={() => setShowEvalModal(true)}
 className={`flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-semibold transition-colors `}>
 <FiTarget />{t("evaluate_campaign")}
 </button>
 </div>
 </div>
 </div>

 {/* ── Stat cards ───────────────────────────────── */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
 {[
 { icon: <FiTarget className="text-blue-400" />, label: t("total_evaluations"), val: campaign.decisionsCount || 0, sub: null },
 { icon: <FiCalendar className="text-purple-400" />, label: t("created"), val: formatDate(campaign.createdAt), sub: null },
 decisions[0] ? {
 icon: DECISION_STYLES[decisions[0].decision]?.icon
 ? <span className={DECISION_STYLES[decisions[0].decision].cls}>{DECISION_STYLES[decisions[0].decision].icon}</span>
 : <FiBarChart2 className={darkMode ? "text-gray-400" : "text-gray-500"} />,
 label: t("latest_decision"),
 val: t(`decision_${decisions[0].decision?.toLowerCase()}`),
 cls: DECISION_STYLES[decisions[0].decision]?.cls,
 sub: formatDate(decisions[0].createdAt),
 } : { icon: <FiBarChart2 className={darkMode ? "text-gray-400" : "text-gray-500"} />, label: t("latest_decision"), val: "—", sub: null },
 { icon: <FiBarChart2 className="text-green-400" />,
 label: lang === "ar" ? "استمرار / إيقاف" : "Continue / Stop",
 val: `${counts.CONTINUE || 0} / ${counts.STOP || 0}`,
 // Read from counts.ADJUST (canonical key after the fold above) and
 // label it "تعديل" / "adjust" to match the rest of the dashboard.
 sub: lang === "ar" ? `${counts.ADJUST || 0} تعديل` : `${counts.ADJUST || 0} adjust`,
 },
 ].map((s, i) => (
 <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
 className={`${darkMode ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"} border rounded-xl p-4`}>
 <div className={`flex items-center gap-2 mb-2 `}>
 <span className="text-lg">{s.icon}</span>
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs`}>{s.label}</p>
 </div>
 <p className={`text-xl font-bold ${s.cls || (darkMode ? "text-white" : "text-gray-900")}`}>{s.val}</p>
 {s.sub && <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-0.5`}>{s.sub}</p>}
 </motion.div>
 ))}
 </div>

 {/* ══ HISTORY SECTION ═════════════════════════════════ */}
 <div className={`${darkMode ? "bg-gray-800/30 border-gray-700" : "bg-white border-gray-200"} border rounded-2xl p-6`}>

 {/* Section header */}
 <div className={`flex items-center justify-between mb-5 `}>
 <div className={`flex items-center gap-2 `}>
 <FiClock className="text-blue-400" />
 <h2 className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{t("decision_history")}</h2>
 {decisions.length > 0 && (
 <span className={`text-xs ${darkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"} px-2 py-0.5 rounded-full`}>{decisions.length}</span>
 )}
 </div>
 <button onClick={() => setShowEvalModal(true)}
 className={`flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm transition-colors `}>
 <FiTarget className="text-xs" />{t("evaluate_this_campaign")}
 </button>
 </div>

 {/* Filter pills */}
 {decisions.length > 0 && (
 <div className={`flex flex-wrap gap-2 mb-5 `}>
 <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs self-center`}><FiFilter className="inline mr-1" /></span>
 {FILTER_OPTIONS.map(opt => {
 const style = DECISION_STYLES[opt];
 return (
 <button key={opt} onClick={() => { setDecisionFilter(opt); setVisibleCount(PAGE_SIZE); }}
 className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all  ${
 decisionFilter === opt
 ? opt === "all" ? "bg-blue-600 text-white border-blue-600" : `${style?.bg} ${style?.cls} border-current`
 : "border-gray-600 text-gray-400 hover:border-gray-500"
 }`}>
 {style && <span>{style.icon}</span>}
 <span>{opt === "all" ? t("all_label") : t(`decision_${opt.toLowerCase()}`)}</span>
 <span className="opacity-60">({counts[opt] || 0})</span>
 </button>
 );
 })}
 </div>
 )}

 {/* Empty state */}
 {decisions.length === 0 ? (
 <div className="text-center py-16">
 <FiTarget className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-5xl mx-auto mb-4`} />
 <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} mb-2`}>{t("no_evaluations_yet")}</p>
 <p className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-sm mb-6`}>{t("history_empty_hint")}</p>
 <button onClick={() => setShowEvalModal(true)}
 className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors">
 {t("run_decision")}
 </button>
 </div>
 ) : filtered.length === 0 ? (
 <div className={`text-center py-12 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
 <p className={darkMode ? "text-gray-500" : "text-gray-400"}>{t("no_history_filter_match")}</p>
 <button onClick={() => setDecisionFilter("all")} className="text-blue-400 text-sm mt-2 hover:text-blue-300">{t("clear_filters")}</button>
 </div>
 ) : (
 <>
 {/* Timeline */}
 <div className="space-y-1">
{grouped.map((item, idx) => {
  if (item.type === "day") {
    return (
      <div key={`day-${idx}`} className="flex items-center gap-3 py-3">
        <div className={`flex-1 h-px ${darkMode ? "bg-gray-700/60" : "bg-gray-300"}`} />
        <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs whitespace-nowrap`}>
          {item.label}
        </span>
        <div className={`flex-1 h-px ${darkMode ? "bg-gray-700/60" : "bg-gray-300"}`} />
      </div>
    );
  }

  const dec = item.data;
  const style = DECISION_STYLES[dec.decision] || DECISION_STYLES.CONTINUE;
  // Show ALL primary KPIs the engine emits, even ones that came back
  // null. The earlier behaviour of `.filter(v !== null)` hid metrics
  // that the user would expect to see (e.g. an Agency campaign without
  // CAC data would show only CPL — but the engine still emits ROAS,
  // CPC, CPM, etc. as null). The PDF report has always shown all 8;
  // the dashboard now matches. KPI_SKIP still filters out true input
  // fields (spend, revenue, leads, etc.) that aren't visual KPIs.
  const kpiEntries = dec.kpis
    ? Object.entries(dec.kpis).filter(([k]) => !KPI_SKIP.has(k))
    : [];
  const isExp = expanded[dec.id];

  return (
    <motion.div
      key={dec.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.02 * idx }}
      className={`rounded-xl border ${style.bg} overflow-hidden`}
    >
      {/* Card header — always visible */}
      <div className="p-5">
        <div className="flex items-start justify-between">
          {/* Left: verdict + date */}
          <div className="flex items-center gap-3">
            <span className={`text-2xl ${style.cls}`}>{style.icon}</span>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-bold text-lg ${style.cls}`}>
                  {t(`decision_${dec.decision?.toLowerCase()}`)}
                </span>
                {dec.status && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusBadgeCls(dec.status)}`}>
                    {getStatusLabel(dec.status, lang)}
                  </span>
                )}
              </div>
              <p className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs mt-0.5`}>
                {formatDate(dec.createdAt, true)}
              </p>
            </div>
          </div>

          {/* Right: share + PDF + expand */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => canShareNow && handleShare(dec)}
              disabled={!canShareNow}
              title={
                !shareFeatureEnabled ? t("upgrade_for_pdf") || (lang === "ar" ? "غير متاح في خطتك" : "Not available on your plan")
                : !shareQuotaOk      ? (lang === "ar" ? "تم بلوغ حد روابط المشاركة" : "Share link limit reached")
                                     : t("share")
              }
              className={`p-2 rounded-lg transition-colors ${
                !canShareNow
                  ? `${darkMode ? "text-gray-600" : "text-gray-300"} cursor-not-allowed`
                  : `${darkMode ? "text-gray-400" : "text-gray-500"} hover:text-blue-400 hover:bg-blue-500/10`
              }`}
            >
              {!shareFeatureEnabled
                ? <FiLock className="text-sm" />
                : <FiShare2 className="text-sm" />}
            </button>
            <button
              onClick={() => canPDFNow && generatePDF(dec, campaign, lang)}
              disabled={pdfGenerating || !canPDFNow}
              title={
                !pdfFeatureEnabled ? t("upgrade_for_pdf") || (lang === "ar" ? "تصدير PDF غير متاح في خطتك — قم بالترقية" : "PDF export not available on your plan — upgrade")
                : !pdfQuotaOk      ? (lang === "ar" ? "تم بلوغ الحد الشهري لتصدير PDF" : "Monthly PDF limit reached")
                                   : t("download_pdf")
              }
              className={`p-2 rounded-lg transition-colors disabled:opacity-40 ${
                !canPDFNow
                  ? `${darkMode ? "text-gray-600" : "text-gray-300"} cursor-not-allowed`
                  : `${darkMode ? "text-gray-400" : "text-gray-500"} hover:text-emerald-400 hover:bg-emerald-500/10`
              }`}
            >
              {!pdfFeatureEnabled
                ? <FiLock className="text-sm" />
                : <FiDownload className="text-sm" />}
            </button>
            <button
              onClick={() => toggleExpanded(dec.id)}
              className={`p-2 ${darkMode ? "text-gray-400 hover:text-white hover:bg-gray-700/60" : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"} rounded-lg transition-colors`}
            >
              {isExp ? <FiChevronUp className="text-sm" /> : <FiChevronDown className="text-sm" />}
            </button>
          </div>
        </div>

        {/* Reason preview (always visible, truncated when collapsed) */}
        {dec.reason && (() => {
          const localizedReason = getLocalizedReason(dec, lang);
          const showEnBadge = lang === "ar" && !dec.reasonAr;
          return (
            <div className="mt-3">
              {showEnBadge && (
                <span className="inline-block text-xs bg-gray-700 text-gray-400 px-2 py-0.5 rounded mb-1">
                  EN
                </span>
              )}
              <p
                className={`${darkMode ? "text-gray-300" : "text-gray-700"} text-sm leading-relaxed ${!isExp ? "line-clamp-2" : ""}`}
                dir={showEnBadge ? "ltr" : undefined}
              >
                {localizedReason}
              </p>
            </div>
          );
        })()}

        {/* KPI preview */}
        {kpiEntries.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-3">
            {/* In the collapsed preview we prefer KPIs that have a
                real value — showing four em-dashes in a row is
                visually noisy and not informative. The expanded view
                below still shows every KPI the engine emits (matching
                the PDF report). */}
            {(() => {
              const hasValue   = kpiEntries.filter(([, v]) => v !== null && v !== undefined);
              const isNull     = kpiEntries.filter(([, v]) => v === null || v === undefined);
              const preview    = hasValue.length >= 4
                ? hasValue.slice(0, 4)
                : [...hasValue, ...isNull].slice(0, 4);
              const remaining  = kpiEntries.length - preview.length;
              return (
                <>
                  {preview.map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1">
                      <span className={`${darkMode ? "text-gray-500" : "text-gray-400"} text-xs uppercase`}>
                        {kpiLabel(k, lang)}
                      </span>
                      <span className={`text-xs font-semibold ${
                        v === null || v === undefined
                          ? darkMode ? "text-gray-500" : "text-gray-400"
                          : typeof v === "number"
                            ? kpiGood(k, v) ? "text-green-400" : "text-red-400"
                            : darkMode ? "text-white" : "text-gray-900"
                      }`}>
                        {fmtKPI(k, v, lang)}
                      </span>
                    </div>
                  ))}
                  {remaining > 0 && (
                    <span className={`${darkMode ? "text-gray-600" : "text-gray-400"} text-xs`}>
                      +{remaining} {lang === "ar" ? "آخر" : "more"}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {isExp && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className={`overflow-hidden border-t ${darkMode ? "border-gray-700/40" : "border-gray-200"}`}
          >
            <div className="p-5 space-y-4">

              {/* Full KPI grid */}
              {kpiEntries.length > 0 && (
                <div>
                  <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs uppercase tracking-wide mb-3`}>
                    {t("key_performance")}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
  {kpiEntries.map(([k, v]) => (
    <div
      key={k}
      className={`${
        darkMode
          ? "bg-gray-900/40 border-gray-700/40"
          : "bg-white border-gray-200 shadow-sm"
      } rounded-xl p-3 border`}
    >
      <p className={`${
        darkMode ? "text-gray-500" : "text-gray-400"
      } text-xs uppercase mb-1`}>
        {kpiLabel(k, lang)}
      </p>
      <p className={`text-lg font-bold ${
        (typeof v === "number" && !kpiGood(k, v)) || (typeof v === "number" && v < 0)
          ? "text-red-400"
          : v === null || v === undefined
            // Render the em-dash dimmer than real values so the user
            // can distinguish "no data" from "0" at a glance.
            ? darkMode ? "text-gray-600" : "text-gray-400"
            : darkMode ? "text-white" : "text-gray-900"
      }`}>
        {fmtKPI(k, v, lang)}
      </p>
    </div>
  ))}
</div>
                </div>
              )}

              {/* Next actions */}
              {dec.nextActions?.length > 0 && (() => {
                const localizedActions = getLocalizedActions(dec, lang);
                return (
                  <div>
                    <p className={`${darkMode ? "text-gray-400" : "text-gray-500"} text-xs uppercase tracking-wide mb-3`}>
                      {t("recommended_actions")}
                    </p>
                    <div className="space-y-2">
                      {localizedActions.map((a, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-blue-600/30 text-blue-400 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <p className={`${darkMode ? "text-gray-300" : "text-gray-700"} text-sm`}>{a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Next review */}
              {dec.nextReviewDays && (
                <div className="flex items-center gap-2 text-sm">
                  <FiClock className={darkMode ? "text-gray-500" : "text-gray-400"} />
                  <span className={darkMode ? "text-gray-400" : "text-gray-500"}>{t("next_review")}:</span>
                  <span className={`${darkMode ? "text-white" : "text-gray-900"} font-medium`}>
                    {dec.nextReviewDays} {t("days")}
                  </span>
                </div>
              )}

              {/* Days evaluated */}
              {dec.days && (
                <div className="flex items-center gap-2 text-sm">
                  <FiCalendar className={darkMode ? "text-gray-500" : "text-gray-400"} />
                  <span className={darkMode ? "text-gray-400" : "text-gray-500"}>{t("evaluation_period")}:</span>
                  <span className={`${darkMode ? "text-white" : "text-gray-900"} font-medium`}>
                    {dec.days} {t("days")}
                  </span>
                </div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
})}
 </div>

 {/* Load more */}
 {visibleCount < filtered.length && (
 <button onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
 className={`w-full mt-5 py-3 ${darkMode ? "text-gray-400 hover:text-white border-gray-700 hover:border-gray-500" : "text-gray-500 hover:text-gray-900 border-gray-300 hover:border-gray-400"} border rounded-xl text-sm transition-colors flex items-center justify-center gap-2`}>
 <FiChevronDown />
 {t("load_more")} ({filtered.length - visibleCount} {t("remaining")})
 </button>
 )}

 {/* Showing count */}
 <p className={`text-center ${darkMode ? "text-gray-600" : "text-gray-400"} text-xs mt-3`}>
 {lang === "ar"
 ? `عرض ${Math.min(visibleCount, filtered.length)} من ${filtered.length}`
 : `Showing ${Math.min(visibleCount, filtered.length)} of ${filtered.length}`}
 </p>
 </>
 )}
 </div>
 </div>

 {/* Modals */}
 {showEvalModal && <EvaluateCampaignModal campaign={campaign} businessType={bizType} onClose={() => setShowEvalModal(false)} onSuccess={handleEvalSuccess} />}
 {showResultModal && latestResult && (
 <DecisionResult decision={latestResult}
 campaign={campaign}
 onShare={() => { setShowResultModal(false); handleShare({ id: latestResult.decisionId, ...latestResult }); }}
 onClose={() => setShowResultModal(false)} />
 )}
 {showShareModal && shareTarget && (
 <ShareDecisionModal decisionId={shareTarget.id || shareTarget.decisionId} onClose={() => setShowShareModal(false)} />
 )}
 </div>
 );
};

export default CampaignDetailPage;